import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { Menu, X, Search, Globe, ChevronDown } from 'lucide-react'

import logo from '@/images/logo.png'

import Button from '../../ui/Button'
import LanguageSwitch from './LanguageSwitch'
import SearchBar from './SearchBar'

const Header = () => {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [moreMenuOpen, setMoreMenuOpen] = useState(false)

  // 判断是否为中文或英文
  const isChineseOrEnglish = ['zh-CN', 'en-US', 'en-SG', 'en'].includes(i18n.language)

  // "更多"菜单中的导航项（版权登记和国际资讯，仅在非中文/英文时显示）
  const conditionalMoreNavigation = isChineseOrEnglish ? [] : [
    { name: t('nav.copyright'), href: '/copyright' },
    { name: t('nav.international'), href: '/international' },
  ]

  // 主要导航菜单（一级菜单）
  const primaryNavigation = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.products'), href: '/products' },
    ...(isChineseOrEnglish ? [
      { name: t('nav.copyright'), href: '/copyright' },
      { name: t('nav.international'), href: '/international' },
    ] : []),
    { name: t('nav.activities'), href: '/activities' },
    { name: t('nav.achievements'), href: '/achievements' },
    { name: t('nav.studios'), href: '/studios' },
  ]

  // 次要导航菜单（二级下拉菜单）
  const secondaryNavigation = [
    ...conditionalMoreNavigation,
    { name: t('nav.talents'), href: '/talents' },
    { name: t('nav.partners'), href: '/partners' },
    { name: t('nav.about'), href: '/about' },
  ]

  // 检查当前路径是否在次要菜单中
  const isSecondaryActive = secondaryNavigation.some(item => item.href === location.pathname)

  // 处理更多菜单的点击
  const handleMoreMenuToggle = () => {
    setMoreMenuOpen(!moreMenuOpen)
  }

  // 处理次要菜单项点击
  const handleSecondaryItemClick = () => {
    setMoreMenuOpen(false)
  }

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="container section-padding">
        <div className="flex items-center">
          {/* Left: Logo */}
          <div className="flex items-center flex-shrink-0 max-w-48">
            <Link to="/" className="flex items-center space-x-2">
              <div className="h-12 w-12 flex items-center justify-center flex-shrink-0">
                <img src={logo} alt='logo' className='h-12 w-12' />
              </div>
              <div className="hidden sm:block min-w-0">
                <span className="font-semibold text-sm xl:text-base leading-tight text-gray-900 block">
                  中国-东盟版权
                </span>
                <span className="font-semibold text-sm xl:text-base leading-tight text-gray-900 block">
                  贸易服务平台
                </span>
              </div>
            </Link>
          </div>

          {/* Center: Desktop Navigation */}
          <nav className="hidden lg:flex flex-1 justify-center px-2 xl:px-4">
            <div className="flex items-center space-x-1">
              {/* 主要导航菜单 */}
              {primaryNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={clsx(
                    'px-2 xl:px-3 py-2 rounded-md text-sm xl:text-base font-medium transition-colors whitespace-nowrap flex-shrink-0',
                    location.pathname === item.href
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  )}
                >
                  {item.name}
                </Link>
              ))}

              {/* 更多菜单（下拉） */}
              <div className="relative flex-shrink-0">
                <button
                  onClick={handleMoreMenuToggle}
                  className={clsx(
                    'flex items-center px-2 xl:px-3 py-2 rounded-md text-sm xl:text-base font-medium transition-colors whitespace-nowrap',
                    isSecondaryActive || moreMenuOpen
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  )}
                >
                  {t('nav.more')}
                  <ChevronDown
                    size={16}
                    className={clsx(
                      'ml-1 transition-transform duration-200',
                      moreMenuOpen && 'rotate-180'
                    )}
                  />
                </button>

                {/* 下拉菜单 */}
                {moreMenuOpen && (
                  <>
                    {/* 背景遮罩 */}
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setMoreMenuOpen(false)}
                    />

                    {/* 下拉菜单内容 */}
                    <div className="absolute top-full left-0 mt-1 w-40 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-20">
                      {secondaryNavigation.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={handleSecondaryItemClick}
                          className={clsx(
                            'block w-full text-left px-4 py-2 text-sm xl:text-base transition-colors',
                            'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          )}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </nav>

          {/* Right: Search, Language, Login */}
          <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0 min-w-0">
            {/* Search Bar - 使用展开式搜索 */}
            <div className="hidden md:block min-w-0">
              <SearchBar />
            </div>

            {/* Language Switch */}
            <div className="min-w-0">
              <LanguageSwitch />
            </div>

            {/* Login Button */}
            <a
              href="http://www.cacopyright.cn/admin/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-2 md:px-3 py-2 text-sm md:text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 whitespace-nowrap flex-shrink-0"
            >
              <span className="hidden xl:inline">{t('nav.login')}</span>
              <span className="xl:hidden">{t('nav.loginShort', '登录')}</span>
            </a>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 flex-shrink-0"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 pt-4 pb-4">
            {/* Mobile Search */}
            <div className="mb-4">
              <SearchBar />
            </div>

            {/* Mobile Nav Items */}
            <nav className="space-y-1">
              {/* 主要菜单 */}
              {primaryNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={clsx(
                    'block px-3 py-2 rounded-md text-base font-medium transition-colors',
                    location.pathname === item.href
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}

              {/* 分隔线 */}
              <div className="border-t border-gray-200 my-2"></div>

              {/* 次要菜单 */}
              <div className="px-3 py-1">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {t('nav.moreFeatures')}
                </span>
              </div>
              {secondaryNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={clsx(
                    'block w-full text-left px-3 py-2 rounded-md text-base font-medium transition-colors',
                    'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  )}
                >
                  {item.name}
                </Link>
              ))}

              {/* 分隔线 */}
              <div className="border-t border-gray-200 my-2"></div>

              {/* Mobile Login Button */}
              <a
                href="http://www.cacopyright.cn/admin/"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium transition-colors text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                {t('nav.login')}
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}

export default Header
